import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "../App";
import { Eye, EyeOff, Loader2, Lock, User, Phone, Mail, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const Login = () => {
  const [mode, setMode] = useState("login"); // "login" or "register"
  const [passcode, setPasscode] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [showPasscode, setShowPasscode] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { loginWithPasscode, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!passcode) {
      toast.error("Please enter your passcode");
      return;
    }

    setLoading(true);
    try {
      await loginWithPasscode(passcode);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid passcode");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!name || !passcode) {
      toast.error("Please enter your name and choose a passcode");
      return;
    }

    if (passcode.length < 3) {
      toast.error("Passcode must be at least 3 digits");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register-customer`, {
        name,
        passcode,
        email: email || undefined,
        phone: phone || undefined
      });
      
      // Auto login after registration
      const { token, user: userData } = response.data;
      localStorage.setItem("token", token);
      
      toast.success("Account created! Welcome to AV Realty Management");
      window.location.href = "/dashboard";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-green-700 p-8 flex-col items-center justify-center">
        <div className="flex flex-col items-center justify-center flex-1">
          <img src="/logo.png" alt="AV Realty Mgmt" className="w-full max-w-lg h-auto" />
        </div>
        <div className="text-center mt-4">
          <p className="text-green-100 text-lg">
            Professional Property Management Solutions
          </p>
        </div>
      </div>

      {/* Right Panel - Login/Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-6">
            <img src="/logo.png" alt="AV Realty Mgmt" className="w-full max-w-xs h-auto" />
          </div>

          {mode === "login" ? (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                  Welcome back
                </h2>
                <p className="text-slate-500">Enter your passcode to continue</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="passcode" className="text-slate-700">Passcode</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="passcode"
                      type={showPasscode ? "text" : "password"}
                      inputMode="numeric"
                      value={passcode}
                      onChange={(e) => setPasscode(e.target.value)}
                      placeholder="Enter passcode"
                      className="h-14 pl-12 pr-12 text-2xl text-center tracking-widest font-mono"
                      data-testid="login-passcode-input"
                      autoComplete="off"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowPasscode(!showPasscode)}
                    >
                      {showPasscode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 text-lg bg-green-700 hover:bg-green-800"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign in"
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-slate-500">
                  New landlord?{" "}
                  <button
                    onClick={() => setMode("register")}
                    className="text-green-700 hover:text-green-800 font-medium"
                  >
                    Create an account
                  </button>
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-semibold text-slate-900 mb-2">
                  Create Account
                </h2>
                <p className="text-slate-500">Set up your property management account</p>
              </div>

              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700">Your Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g., Minhaz Hira"
                      className="h-12 pl-12"
                      data-testid="register-name-input"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reg-passcode" className="text-slate-700">Choose a Passcode *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="reg-passcode"
                      type={showPasscode ? "text" : "password"}
                      inputMode="numeric"
                      value={passcode}
                      onChange={(e) => setPasscode(e.target.value)}
                      placeholder="e.g., 505"
                      className="h-12 pl-12 pr-12 text-xl tracking-widest font-mono"
                      data-testid="register-passcode-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowPasscode(!showPasscode)}
                    >
                      {showPasscode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">This will be your login code</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email (optional)</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="h-12 pl-12"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700">Phone (optional)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                      className="h-12 pl-12"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 text-lg bg-green-700 hover:bg-green-800 mt-6"
                  disabled={loading}
                  data-testid="register-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-slate-500">
                  Already have an account?{" "}
                  <button
                    onClick={() => setMode("login")}
                    className="text-green-700 hover:text-green-800 font-medium"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
